import { helpElement } from '../../../help/attrs';
import React, { useEffect, useMemo, useState } from 'react';
import { SxProps, Theme } from '@mui/material/styles';
import { BuyerOrderService } from '../../../services/buyerOrder.service';
import { IBuyerOrder } from '../../../interfaces/garments/buyerOrder.interface';
import { useToast } from '../../../context/ToastContext';
import RefAutocomplete, { RefOption } from './RefAutocomplete';
import { fetchAllPages } from './fetchAllPages';

interface Props {
    institutionId: string;
    /** When set, only this buyer's orders are offered. */
    buyerId?: string | null;
    /** Selected buyer order id. */
    value: string | null;
    /** Shown for the selected id until the option list has loaded (e.g. the order number stored on the record). */
    valueLabel?: string | null;
    onChange: (id: string | null, order: IBuyerOrder | null) => void;
    label?: string;
    helperText?: string;
    disabled?: boolean;
    sx?: SxProps<Theme>;
    /** Help-mode key; lands on the picker's root element. */
    'data-help'?: string;
}

/**
 * Buyer order picker for the commercial documents (LC, UD, ...). Lists the institution's buyer
 * orders, narrowed to one buyer when `buyerId` is given, and hands back the whole order so a
 * caller can also store its order number.
 */
const BuyerOrderRefPicker: React.FC<Props> = ({
    institutionId, buyerId, value, valueLabel, onChange, label = 'Buyer Order', helperText, disabled, sx, 'data-help': dataHelp,
}) => {
    const toast = useToast();
    const [orders, setOrders] = useState<IBuyerOrder[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const rows = await fetchAllPages((page, size) =>
                    BuyerOrderService.list(institutionId, page, size, buyerId || undefined));
                if (!cancelled) setOrders(rows);
            } catch (e) {
                if (!cancelled) toast.showApiError(e, 'Failed to load buyer orders');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId, buyerId]);

    const options: RefOption[] = useMemo(() => {
        const list = orders.map(o => ({ id: o.id, label: o.buyerPoNumber ? `${o.orderNumber} — ${o.buyerPoNumber}` : o.orderNumber }));
        if (value && valueLabel && !list.some(o => o.id === value)) list.unshift({ id: value, label: valueLabel });
        return list;
    }, [orders, value, valueLabel]);

    return (
        <RefAutocomplete
            label={label}
            options={options}
            loading={loading}
            value={value}
            disabled={disabled}
            helperText={helperText}
            sx={sx}
            {...helpElement(dataHelp)}
            onChange={(id) => onChange(id, id ? orders.find(o => o.id === id) ?? null : null)}
        />
    );
};

export default BuyerOrderRefPicker;
